/**
 * MarkerPopup (Iteration 12 — N7, extended in N11)
 * 
 * Shows location details when a marker is clicked.
 * "Enter count" button expands popup to show InlineCountForm.
 * On success: popup closes, marker updates, scoreboard refreshes.
 * 
 * LAT-25: below_zero + double-submit prevention via InlineCountForm
 * LAT-26: form stays populated on failure
 * LAT-27: writes to manual bucket only
 * 
 * Props:
 *   marker: { id, lat, lng, label, scopeName, scopeId, supplyManual, demandManual, supplyBeacon, demandBeacon }
 *   onClose: () => void
 *   onCountSubmitted: (marker, entry) => void (refresh callback)
 *   supplyMode: 'supply_only' | 'demand_only' | 'both'
 */

import React, { useState } from 'react';
import { Popup } from 'react-map-gl';
import {
  Box,
  Typography,
  Button,
  Divider,
} from '@mui/material';
import { formatCount } from '../../utils/countModel';
import InlineCountForm from './InlineCountForm';

// AST-113: mapbox renders .mapboxgl-popup-content on a WHITE background, but
// this app's theme is dark -- so theme.palette.text.primary (#E8DCDA) was
// near-invisible here, and supply/demand (#316EC0 / #ECAE36) were low
// contrast. Fixed dark values, because this surface is mapbox's white and
// does not follow our palette. Mark reported this on the mat-count pills
// (AST-113); this popup had the identical bug on the Manual Map screen.
const POPUP_TEXT = '#1A1A1A';
const POPUP_TEXT_MUTED = '#5A5A5A';
const POPUP_SUPPLY = '#1B4F8A';   // darker info -- readable on white
const POPUP_DEMAND = '#8A5A00';   // darker canary -- readable on white

export default function MarkerPopup({
  marker,
  onClose,
  onCountSubmitted,
  supplyMode = 'both',
}) {
  const [showForm, setShowForm] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  if (!marker) return null;

  const supplyManual = marker.supplyManual ?? 0;
  const demandManual = marker.demandManual ?? 0;
  const hasCounts = supplyManual > 0 || demandManual > 0;

  const handleSubmitCount = async ({ supplyDelta, demandDelta, idempotencyKey }) => {
    setSubmitLoading(true);
    setSubmitError(null);

    try {
      // TODO: Wire to API — submitEntry with channel: 'desktop'
      console.warn('[FIXTURE] Desktop count entry:', {
        projectId: marker.projectId,
        scopeId: marker.scopeId,
        locationId: marker.id,
        supplyDelta,
        demandDelta,
        channel: 'desktop',
        idempotencyKey,
      });

      // Simulate API delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Notify parent to refresh
      if (onCountSubmitted) {
        onCountSubmitted(marker, { supplyDelta, demandDelta });
      }

      // Close popup on success
      onClose();
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit count');
      throw err; // Re-throw so InlineCountForm keeps form populated (LAT-26)
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Popup
      latitude={marker.lat}
      longitude={marker.lng}
      anchor="top"
      onClose={onClose}
      closeOnClick={false}
      maxWidth={showForm ? '320px' : '280px'}
    >
      <Box sx={{ p: 1, minWidth: 200, color: POPUP_TEXT }}>
        {/* Location name */}
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: POPUP_TEXT }}
        >
          {marker.label || 'Unnamed Location'}
        </Typography>

        {/* Scope name */}
        {marker.scopeName && (
          <Typography variant="body2" sx={{ mb: 1, color: POPUP_TEXT_MUTED }}>
            {marker.scopeName}
          </Typography>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Counts */}
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ color: POPUP_TEXT_MUTED }}>
              Supply
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: POPUP_SUPPLY }}>
              {formatCount(supplyManual)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: POPUP_TEXT_MUTED }}>
              Demand
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: POPUP_DEMAND }}>
              {formatCount(demandManual)}
            </Typography>
          </Box>
        </Box>

        {!hasCounts && (
          <Typography variant="caption" sx={{ fontStyle: 'italic', color: POPUP_TEXT_MUTED }}>
            No counts yet
          </Typography>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Enter count form (N11 extension) */}
        {showForm ? (
          <InlineCountForm
            supplyMode={supplyMode}
            onSubmit={handleSubmitCount}
            loading={submitLoading}
            error={submitError}
            currentCounts={{ supplyManual, demandManual }}
            compact
          />
        ) : (
          <Button
            variant="contained"
            size="small"
            fullWidth
            onClick={() => setShowForm(true)}
            sx={{ textTransform: 'none' }}
          >
            Enter Count
          </Button>
        )}
      </Box>
    </Popup>
  );
}
